'use client';

import { c as _c } from "react/compiler-runtime";
import './commitment-lists.scss';
import { useQueryState } from 'nuqs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { CommitmentFrequencyEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import { type GraphQLPledge } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.ts';
import { hasSupporterDataIssues, useSupporterDataStatus } from '@sola/supporter-sdk/hooks/data-status.ts';
import { commitmentsQuery, pledgesQuery } from '@sola/supporter-sdk/queries.ts';
import { groupCommitmentsByFrequency, sortCommitmentsByPriority } from '@sola/supporter-sdk/utils/commitments.ts';
import { CommitmentCard } from '../../molecules/commitment-card/commitment-card.tsx';
import { PledgeCard } from '../../molecules/pledge-card/pledge-card.tsx';
import { PrimerError } from '../../molecules/primer-error/primer-error.tsx';
import { SectionContent } from '../../molecules/section-content/section-content.tsx';
import { PaymentIssueAlert } from '../payment-issue-alert/payment-issue-alert.tsx';
import { type CommitmentCardData, getCommitmentTotal } from './commitment-lists.logic.ts';
const getCardElementId = (id: string) => `commitment-card-${id}`;

/**
 * Get component data
 */
export const useComponentData = () => {
  const {
    t
  } = useTranslation('my');
  const {
    data,
    isLoading,
    isFetching,
    isPending,
    isError
  } = useSupporterQuery([commitmentsQuery, pledgesQuery]);
  const {
    data: dataStatus
  } = useSupporterDataStatus();
  let commitmentFrequencyGroups: CommitmentCardData[] = [];
  const [commitments = [], pledges = []] = data;
  const hasDataIssue = hasSupporterDataIssues(dataStatus, ['commitments', 'pledges']);
  const hasError = isError || hasDataIssue;
  if (commitments?.length) {
    const {
      [CommitmentFrequencyEnum.Monthly]: monthlyCommitments,
      [CommitmentFrequencyEnum.Quarterly]: quarterlyCommitments,
      [CommitmentFrequencyEnum.Semiannually]: semiannualCommitments,
      [CommitmentFrequencyEnum.Annually]: annualCommitments
    } = groupCommitmentsByFrequency(commitments);
    commitmentFrequencyGroups = [{
      heading: t('commitments.headings.monthly'),
      frequencyAbbr: t('commitments.frequencyAbbr.monthly'),
      commitments: monthlyCommitments
    }, {
      heading: t('commitments.headings.quarterly'),
      frequencyAbbr: t('commitments.frequencyAbbr.quarterly'),
      commitments: quarterlyCommitments
    }, {
      heading: t('commitments.headings.semiannually'),
      frequencyAbbr: t('commitments.frequencyAbbr.semiannually'),
      commitments: semiannualCommitments
    }, {
      heading: t('commitments.headings.annually'),
      frequencyAbbr: t('commitments.frequencyAbbr.annually'),
      commitments: annualCommitments
    }];
  }
  const isShowingSkeletons = (isLoading || isFetching || isPending) && !hasError;
  return {
    isShowingSkeletons,
    commitmentFrequencyGroups,
    hasError,
    pledges
  };
};

// TODO: Might be worthwhile to extract PledgeList from this component - which would allow for separating it from the Commitment List
type PledgeListProps = {
  pledges: GraphQLPledge[];
};
const PledgeList: React.FC<PledgeListProps> = t0 => {
  const $ = _c(19);
  const {
    pledges
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      history: "push"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [activePledgeId, setPledgeIdQuery] = useQueryState("id", t2);
  let t3;
  if ($[2] !== setPledgeIdQuery) {
    t3 = (open, pledgeId) => {
      if (open) {
        setPledgeIdQuery(pledgeId);
      } else {
        setPledgeIdQuery(null);
      }
    };
    $[2] = setPledgeIdQuery;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const setActiveQueryId = t3;
  let t4;
  if ($[4] !== activePledgeId) {
    t4 = () => {
      if (!activePledgeId) {
        return;
      }
      const activeCard = document.getElementById(getCardElementId(activePledgeId));
      if (!activeCard) {
        return;
      }
      activeCard.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    };
    $[4] = activePledgeId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== t) {
    t6 = t("financialPledges");
    $[7] = t;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== activePledgeId || $[10] !== pledges || $[11] !== setActiveQueryId) {
    let t8;
    if ($[13] !== activePledgeId || $[14] !== setActiveQueryId) {
      t8 = pledge => {
        if (pledge.pledgeId === null) {
          return null;
        }
        const id = getCardElementId(pledge.pledgeId);
        return <div id={id} key={id}><PledgeCard pledge={pledge} onActiveChange={setActiveQueryId} isActive={pledge.pledgeId === activePledgeId} /></div>;
      };
      $[13] = activePledgeId;
      $[14] = setActiveQueryId;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    t7 = pledges.map(t8);
    $[9] = activePledgeId;
    $[10] = pledges;
    $[11] = setActiveQueryId;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = <div key="commitment-group-pledge" className="CommitmentLists__group"><SectionContent heading={t6}>{t7}</SectionContent></div>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
};
export type CommitmentListGroupsProps = {
  commitmentFrequencyGroups: CommitmentCardData[];
};
const CommitmentListGroups: React.FC<CommitmentListGroupsProps> = t0 => {
  const $ = _c(15);
  const {
    commitmentFrequencyGroups
  } = t0;
  const {
    t
  } = useTranslation("core");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      history: "push"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [activeCommitmentId, setCommitmentIdQuery] = useQueryState("id", t1);
  let t2;
  if ($[1] !== setCommitmentIdQuery) {
    t2 = (open, commitmentId) => {
      if (open) {
        setCommitmentIdQuery(commitmentId);
      } else {
        setCommitmentIdQuery(null);
      }
    };
    $[1] = setCommitmentIdQuery;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const setActiveQueryId = t2;
  let t3;
  if ($[3] !== activeCommitmentId) {
    t3 = () => {
      if (!activeCommitmentId) {
        return;
      }
      const activeCard = document.getElementById(getCardElementId(activeCommitmentId));
      if (!activeCard) {
        return;
      }
      activeCard.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    };
    $[3] = activeCommitmentId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== activeCommitmentId || $[7] !== commitmentFrequencyGroups || $[8] !== setActiveQueryId || $[9] !== t) {
    let t6;
    if ($[11] !== activeCommitmentId || $[12] !== setActiveQueryId || $[13] !== t) {
      t6 = t7 => {
        const {
          heading,
          frequencyAbbr,
          commitments: commitmentsByFrequency
        } = t7;
        if (!commitmentsByFrequency?.length) {
          return null;
        }
        const commitmentsByType = sortCommitmentsByPriority(commitmentsByFrequency, t);
        return !!commitmentsByType?.length && <div className="CommitmentLists__group" key={`commitment-group-${heading}`}><SectionContent heading={heading} subheading={<>{t("numbers.currencyCustom", {
              amount: getCommitmentTotal(commitmentsByFrequency)
            })}<span className="label-neutral-mild">{frequencyAbbr}</span></>}>{commitmentsByType.map(commitment => {
              const id = getCardElementId(commitment.internalId);
              return <div id={id} key={id}><CommitmentCard commitment={commitment} onActiveChange={setActiveQueryId} isActive={commitment.internalId === activeCommitmentId} /></div>;
            })}</SectionContent></div>;
      };
      $[11] = activeCommitmentId;
      $[12] = setActiveQueryId;
      $[13] = t;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    t5 = commitmentFrequencyGroups.map(t6);
    $[6] = activeCommitmentId;
    $[7] = commitmentFrequencyGroups;
    $[8] = setActiveQueryId;
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
const CommitmentListsEmptyState: React.FC = () => {
  const $ = _c(17);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["my", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  let t1;
  if ($[1] !== t) {
    t1 = t("my:commitments.noCommitments.title");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <h3 className="heading-sm mb-snug-xl">{t1}</h3>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("my:commitments.noCommitments.message");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <p className="body-sm label-neutral-mild">{t3}</p>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("my:commitments.noCommitments.ctaText");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <p className="flex"><ButtonStyle className="" size="md" variant="primary"><a href={`${process.env.NEXT_PUBLIC_BASE_URL_DOTCOM}/sponsor-a-child/`}>{t5}</a></ButtonStyle></p>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t2 || $[14] !== t4 || $[15] !== t6) {
    t7 = <>{t2}{t4}{t6}</>;
    $[13] = t2;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
const CommitmentListsContent: React.FC = () => {
  const $ = _c(32);
  const {
    isShowingSkeletons,
    commitmentFrequencyGroups,
    hasError,
    pledges
  } = useComponentData();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["my"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  if (isShowingSkeletons) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <SectionContent isLoadingContent={true} isLoadingHeading={true} />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const hasPledges = !!pledges?.length;
  const hasCommitments = !!commitmentFrequencyGroups?.length;
  if (hasError && !hasCommitments && !hasPledges) {
    let t1;
    if ($[2] !== t) {
      t1 = t("commitments.queryError.title");
      $[2] = t;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== t) {
      t2 = t("commitments.queryError.message");
      $[4] = t;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== t) {
      t3 = t("my:ctas.contactSupport.url");
      $[6] = t;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== t) {
      t4 = t("my:ctas.contactSupport.text");
      $[8] = t;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== t3 || $[11] !== t4) {
      t5 = <a href={t3}>{t4}</a>;
      $[10] = t3;
      $[11] = t4;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== t1 || $[14] !== t2 || $[15] !== t5) {
      t6 = <Alert variant="negative" title={t1} message={t2} action={t5} />;
      $[13] = t1;
      $[14] = t2;
      $[15] = t5;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    return t6;
  }
  let t1;
  let t2;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <PrimerError />;
    t2 = <PaymentIssueAlert />;
    $[17] = t1;
    $[18] = t2;
  } else {
    t1 = $[17];
    t2 = $[18];
  }
  let t3;
  if ($[19] !== hasPledges || $[20] !== pledges) {
    t3 = hasPledges && <PledgeList pledges={pledges} />;
    $[19] = hasPledges;
    $[20] = pledges;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] !== commitmentFrequencyGroups || $[23] !== hasCommitments) {
    t4 = hasCommitments && <CommitmentListGroups commitmentFrequencyGroups={commitmentFrequencyGroups} />;
    $[22] = commitmentFrequencyGroups;
    $[23] = hasCommitments;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  let t5;
  if ($[25] !== hasCommitments || $[26] !== hasPledges) {
    t5 = !hasPledges && !hasCommitments && <CommitmentListsEmptyState />;
    $[25] = hasCommitments;
    $[26] = hasPledges;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  let t6;
  if ($[28] !== t3 || $[29] !== t4 || $[30] !== t5) {
    t6 = <>{t1}{t2}{t3}{t4}{t5}</>;
    $[28] = t3;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  return t6;
};

/**
 * Displays list of commitments based on frequency.
 */
export const CommitmentLists = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="CommitmentLists max-w-md flex flex-col gap-comfy-xl"><CommitmentListsContent /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};